import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { SubPageLayout } from '@/components/layout/SubPageLayout';
import { useRollReviewTransactionDetail } from '@/features/wallet/hooks/useWallet';
import { formatCurrency } from '@/lib/pricing';
import { isRefundedStatus } from '@/features/wallet/utils/mappers';
import type { RollReviewStatus } from '@/types/wallet';

// ── Formatters ────────────────────────────────────────────────────────────────

/** "Apr 19, 2026" — UTC-stable so the same instant shows the same date everywhere. */
function fmtDateShort(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}

/** "Apr 19, 2026 · 14:32" — 24 h, UTC. */
function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const h = d.getUTCHours().toString().padStart(2, '0');
  const m = d.getUTCMinutes().toString().padStart(2, '0');
  return `${fmtDateShort(iso)} · ${h}:${m}`;
}

// ── Status map ────────────────────────────────────────────────────────────────

const STATUS_META: Record<RollReviewStatus, { label: string; color: string }> = {
  paid:      { label: 'Awaiting acceptance', color: '#6897FF' },
  in_review: { label: 'In review',           color: '#F39C12' },
  delivered: { label: 'Delivered',            color: '#22c55e' },
  expired:   { label: 'Expired',              color: 'rgba(255,255,255,0.4)' },
  declined:  { label: 'Declined',             color: '#ef4444' },
  cancelled: { label: 'Cancelled',            color: '#ef4444' },
  refunded:  { label: 'Refunded',             color: '#ef4444' },
};

// ── Shared design tokens ──────────────────────────────────────────────────────

const CARD_CLS = 'bg-[#231a1a] border border-white/[0.06] rounded-[16px]';

// ── Primitives ────────────────────────────────────────────────────────────────

function Divider() {
  return <div className="h-px bg-[rgba(255,255,255,0.06)]" />;
}

/** Section label inside a card: "BREAKDOWN", etc. */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[#8d8d8d] text-[11px] font-semibold tracking-[1px] uppercase px-5 pt-[18px] pb-[4px]">
      {children}
    </p>
  );
}

/** One line in the breakdown card. */
function BreakdownRow({
  label,
  value,
  labelColor = 'rgba(255,255,255,0.7)',
  valueColor = '#fff',
  valueBold = false,
  valueSize = 14,
}: {
  label: string;
  value: string;
  labelColor?: string;
  valueColor?: string;
  valueBold?: boolean;
  valueSize?: number;
}) {
  return (
    <div className="flex items-center justify-between px-5 h-[33px]">
      <span style={{ color: labelColor, fontSize: 14 }}>{label}</span>
      <span
        style={{ color: valueColor, fontSize: valueSize }}
        className={valueBold ? 'font-bold' : 'font-medium'}
      >
        {value}
      </span>
    </div>
  );
}

/** One row in the meta details / payout cards. */
function MetaRow({
  label,
  value,
  valueColor = '#fff',
  last = false,
}: {
  label: string;
  value: string;
  valueColor?: string;
  last?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between px-4 py-[12px] ${
        last ? '' : 'border-b border-white/[0.06]'
      }`}
      style={{ minHeight: last ? 40 : 41 }}
    >
      <span className="text-[#8d8d8d] text-[13px]">{label}</span>
      <span className="text-[13px] font-medium text-right" style={{ color: valueColor }}>
        {value}
      </span>
    </div>
  );
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function TransactionSkeleton() {
  return (
    <div className="flex flex-col gap-[14px] pt-2 animate-pulse">
      <div className="h-[125px] rounded-[16px] bg-white/[0.04]" />
      <div className="h-[268px] rounded-[16px] bg-white/[0.04]" />
      <div className="h-[212px] rounded-[16px] bg-white/[0.04]" />
      <div className="h-[89px]  rounded-[16px] bg-white/[0.04]" />
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const TransactionDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { transactionId } = useParams<{ transactionId: string }>();
  const { data: tx, isLoading } = useRollReviewTransactionDetail(transactionId ?? null);

  // ── Derived state ──────────────────────────────────────────────────────────
  const isPending  = tx ? (tx.status === 'paid' || tx.status === 'in_review') : false;
  const isRefunded = tx ? isRefundedStatus(tx.status) : false;
  const isVoid     = tx ? (tx.status === 'declined' || tx.status === 'cancelled' || tx.status === 'expired') : false;
  const statusMeta = tx ? (STATUS_META[tx.status] ?? { label: tx.status, color: 'rgba(255,255,255,0.5)' }) : null;

  const heroLabel = isRefunded ? 'Refunded to student'
    : isPending ? 'You will receive'
    : isVoid    ? 'Not received'
    : 'You received';

  const heroAmountColor = isRefunded ? '#ef4444'
    : isPending ? '#F39C12'
    : isVoid    ? 'rgba(255,255,255,0.3)'
    : '#22c55e';

  const heroAmount = tx
    ? (isRefunded || isVoid ? '—' : formatCurrency(tx.net_received_cents, tx.currency))
    : '';

  const netLabel = isPending ? 'You will receive' : 'You received';
  const netColor = isPending ? '#F39C12' : '#22c55e';

  const showPayoutCard = tx && !isPending && !isRefunded &&
    (!!tx.included_in_payout_date || !!tx.payout_bank_last4);

  const studentFirstName = tx?.student_name.split(' ')[0] ?? 'student';

  return (
    <SubPageLayout title="Transaction" hideTopNav hideBottomNav>
      {/*
        -mx-4 escapes SubPageLayout's px-4.
        Inner content uses px-5 = 20 px from each screen edge (matches Figma).
      */}
      <div className="-mx-4">

        {/* ── Loading ──────────────────────────────────────────────── */}
        {isLoading && (
          <div className="px-5">
            <TransactionSkeleton />
          </div>
        )}

        {/* ── Not found ────────────────────────────────────────────── */}
        {!isLoading && !tx && (
          <div className="flex items-center justify-center py-20 px-5">
            <p className="text-white/40 text-sm">Transaction not found</p>
          </div>
        )}

        {/* ── Content ──────────────────────────────────────────────── */}
        {!isLoading && tx && (
          <div className="flex flex-col gap-[14px] pt-[8px] px-5 pb-28">

            {/* ① Hero card */}
            <div className={`${CARD_CLS} flex flex-col items-center gap-[6px] pt-[23px] pb-[16px] px-[21px]`}>
              <p className="text-[#8d8d8d] text-[11px] tracking-[1px] uppercase text-center w-full">
                {heroLabel}
              </p>
              <p
                className="text-[38px] font-bold leading-none text-center"
                style={{ color: heroAmountColor }}
              >
                {heroAmount}
              </p>
              <p className="text-[#8d8d8d] text-[12px] text-center">
                {fmtDateShort(tx.requested_at)} · {tx.order_ref}
              </p>
            </div>

            {/* ② Breakdown card */}
            <div className={CARD_CLS}>
              <SectionLabel>Breakdown</SectionLabel>

              {/* Gross */}
              <BreakdownRow
                label="Roll Review"
                value={formatCurrency(tx.gross_cents, tx.currency)}
                labelColor="rgba(255,255,255,0.7)"
                valueColor="#fff"
              />

              {/* Platform flat fee + Subtotal — only when fee > 0 */}
              {tx.platform_flat_fee_cents > 0 && (
                <>
                  <BreakdownRow
                    label="Platform fee sossiety"
                    value={`−${formatCurrency(tx.platform_flat_fee_cents, tx.currency)}`}
                    labelColor="rgba(255,255,255,0.7)"
                    valueColor="rgba(255,255,255,0.7)"
                  />
                  <Divider />
                  <BreakdownRow
                    label="Subtotal"
                    value={formatCurrency(tx.subtotal_cents, tx.currency)}
                    labelColor="rgba(255,255,255,0.7)"
                    valueColor="#fff"
                  />
                </>
              )}

              {/* Commission */}
              <BreakdownRow
                label={`Commission sossiety (${tx.commission_percent}%)`}
                value={`−${formatCurrency(tx.commission_cents, tx.currency)}`}
                labelColor="rgba(255,255,255,0.7)"
                valueColor="rgba(255,255,255,0.7)"
              />

              <Divider />

              {/* Net received / will receive */}
              <div className="flex items-center justify-between px-5 py-[8px] h-[36px]">
                <span className="text-[14px] font-semibold text-white">{netLabel}</span>
                <span className="text-[16px] font-bold" style={{ color: netColor }}>
                  {formatCurrency(tx.net_received_cents, tx.currency)}
                </span>
              </div>

              <div className="pb-[4px]" />
            </div>

            {/* ③ Details card */}
            <div className={`${CARD_CLS} pt-[4px]`}>
              {statusMeta && (
                <MetaRow label="Status" value={statusMeta.label} valueColor={statusMeta.color} />
              )}
              <MetaRow label="Student" value={tx.student_name} />
              <MetaRow label="Requested" value={fmtDateTime(tx.requested_at)} />
              {tx.delivered_at && (
                <MetaRow label="Delivered" value={fmtDateTime(tx.delivered_at)} />
              )}
              <MetaRow label="Order ID" value={tx.order_ref} last />
            </div>

            {/* ④ Payout card — only after settlement */}
            {showPayoutCard && (
              <div className={`${CARD_CLS} pt-[4px]`}>
                {tx.included_in_payout_date && (
                  <MetaRow
                    label="Included in payout"
                    value={tx.included_in_payout_date}
                    last={!tx.payout_bank_last4}
                  />
                )}
                {tx.payout_bank_last4 && (
                  <MetaRow
                    label="Payout method"
                    value={`${tx.payout_bank_type ?? 'ACH'} ••••${tx.payout_bank_last4}`}
                    last
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Sticky bottom CTA ─────────────────────────────────────────────── */}
      {!isLoading && tx && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#160c0c] border-t border-white/[0.06] px-4 pb-6 pt-4">
          <div className="max-w-md mx-auto">
            <button
              type="button"
              onClick={() => navigate(`/messages/new?userId=${tx.student_id}`)}
              className="w-full h-[50px] rounded-full bg-[#d72e16] text-white text-[15px] font-semibold flex items-center justify-center gap-2 hover:bg-[#c02612] transition-colors"
            >
              <MessageCircle size={18} />
              Message {studentFirstName}
            </button>
          </div>
        </div>
      )}
    </SubPageLayout>
  );
};

export default TransactionDetailPage;
